import React, { useMemo, useState, useEffect } from 'react';
import Select from 'react-select';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';
import { normalizeImagePath } from '../../utils/mediaUtils';

function displayBatchName(b) {
  return b.batchInternalName || b.batchName || `Batch #${b.batchId}`;
}

function formatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatTime(value) {
  if (!value) return '';
  const parts = String(value).split(':');
  const hourNum = parseInt(parts[0], 10);
  const minuteNum = parseInt(parts[1] || '0', 10);
  if (Number.isNaN(hourNum)) return String(value);
  const period = hourNum >= 12 ? 'PM' : 'AM';
  const hour12 = ((hourNum + 11) % 12) + 1;
  const hourStr = String(hour12).padStart(2, '0');
  const minuteStr = String(Number.isNaN(minuteNum) ? 0 : minuteNum).padStart(2, '0');
  return `${hourStr}:${minuteStr} ${period}`;
}

export default function StudentsAttendance() {
  const [batches, setBatches] = useState([]);
  const [coordinateCourses, setCoordinateCourses] = useState({});
  const [accessibleCourseIds, setAccessibleCourseIds] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imageCache, setImageCache] = useState({});
  const [imageErrors, setImageErrors] = useState({});
  const [expandedStudents, setExpandedStudents] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [subjectWiseByStudent, setSubjectWiseByStudent] = useState({});
  const [loadingSubjectWise, setLoadingSubjectWise] = useState({});
  const [sessionsByStudent, setSessionsByStudent] = useState({});
  const [loadingSessions, setLoadingSessions] = useState({});
  const [selectedSubjectModal, setSelectedSubjectModal] = useState(null);
  const [copiedGroupMsg, setCopiedGroupMsg] = useState(false);
  const [copiedStudentId, setCopiedStudentId] = useState(null);

  // Load coordinate courses / access info
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const accessRes = await axiosInstance.get('/admin/me/coordinate-courses').catch(() => ({ data: {} }));
        if (!isMounted) return;

        const data = accessRes.data || {};
        setCoordinateCourses(data.coordinateCourses || {});
        setAccessibleCourseIds(Array.isArray(data.accessibleCourseIds) ? data.accessibleCourseIds : []);
        setIsSuperAdmin(!!data.isSuperAdmin);
      } catch (e) {
        console.error('Failed to load coordinate courses for StudentsAttendance', e);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  // Load batches (ongoing by default)
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const resp = await axiosInstance.get('/admin/batches', {
          params: { status: 'ongoing' },
        });
        if (!isMounted) return;
        const data = resp.data || {};
        const list = Array.isArray(data.batches) ? data.batches : [];
        setBatches(list);
      } catch (e) {
        console.error('Failed to load batches for StudentsAttendance', e);
        setBatches([]);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const coordinateCourseIds = useMemo(() => {
    if (!coordinateCourses || typeof coordinateCourses !== 'object') return [];
    return Object.keys(coordinateCourses)
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id));
  }, [coordinateCourses]);

  const filteredBatches = useMemo(() => {
    if (!Array.isArray(batches)) return [];
    // If user has no specific coordinate courses and is not restricted, show all batches
    if (!coordinateCourseIds.length) return batches;
    return batches.filter((batch) => {
      if (batch.courseId === null || batch.courseId === undefined) return false;
      const courseIdNum = Number(batch.courseId);
      return !Number.isNaN(courseIdNum) && coordinateCourseIds.includes(courseIdNum);
    });
  }, [batches, coordinateCourseIds]);

  const options = useMemo(() => {
    // Group batches by course
    const grouped = {};
    
    (filteredBatches || []).forEach((b) => {
      const courseKey = (b.course && (b.course.shortName || b.course.fullName)) || 'Other Courses';
      if (!grouped[courseKey]) {
        grouped[courseKey] = [];
      }
      grouped[courseKey].push(b);
    });

    // Convert to react-select grouped format
    return Object.entries(grouped).map(([courseName, courseBatches]) => ({
      label: courseName,
      options: courseBatches.map((b) => {
        const name = displayBatchName(b);
        const groupPart = b.whatsappGroupName ? ` - ${b.whatsappGroupName}` : '';
        const optionCourseName = (b.course && (b.course.shortName || b.course.fullName)) || 'Other';
        return { 
          value: b.batchId, 
          label: `${name}${groupPart}`,
          courseName: optionCourseName
        };
      })
    }));
  }, [filteredBatches]);

  useEffect(() => {
    if (!selectedBatchId) return;
    const stillExists = filteredBatches?.some((b) => b.batchId === selectedBatchId);
    if (!stillExists) {
      setSelectedBatchId('');
    }
  }, [filteredBatches, selectedBatchId]);

  // Fetch attendance data when batch is selected
  useEffect(() => {
    if (!selectedBatchId) {
      setAttendanceData(null);
      return;
    }

    const fetchAttendanceData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axiosInstance.get('/admin/students-attendance/data', {
          params: { batchId: selectedBatchId },
        });
        setAttendanceData(response.data || null);
      } catch (err) {
        console.error('Failed to fetch students attendance data', err);
        const message = err?.response?.data?.error || (err instanceof Error ? err.message : 'An error occurred');
        setError(message);
        setAttendanceData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendanceData();
  }, [selectedBatchId]);

  // Load student image using normalized local filename from /storage/student_images
  const loadStudentImage = (studentId, imagePath) => {
    if (!imagePath || imageCache[studentId] !== undefined) return;

    try {
      const filename = normalizeImagePath(imagePath);
      if (!filename) {
        setImageCache(prev => ({ ...prev, [studentId]: null }));
        setImageErrors(prev => ({ ...prev, [studentId]: true }));
        return;
      }
      // Build API path via backend images route
      const localPath = getImageURL(`/student_images/${encodeURIComponent(filename)}`);
      setImageCache(prev => ({ ...prev, [studentId]: localPath }));
      setImageErrors(prev => ({ ...prev, [studentId]: false }));
    } catch (e) {
      setImageCache(prev => ({ ...prev, [studentId]: null }));
      setImageErrors(prev => ({ ...prev, [studentId]: true }));
    }
  };

  const handleStudentImageError = (studentId) => {
    setImageErrors(prev => ({ ...prev, [studentId]: true }));
  };

  // Load images for visible students
  useEffect(() => {
    if (attendanceData?.students) {
      attendanceData.students.forEach(student => {
        if (student.hasImage && student.imagePath) {
          loadStudentImage(student.studentId, student.imagePath);
        }
      });
    }
  }, [attendanceData]);

  const ensureSubjectWiseLoaded = async (studentId, registrationNumber) => {
    const key = `${studentId}::${registrationNumber}`;

    if (subjectWiseByStudent[key] || loadingSubjectWise[key]) {
      return;
    }

    setLoadingSubjectWise((prev) => ({ ...prev, [key]: true }));
    try {
      const idEnc = encodeURIComponent(studentId);
      const regEnc = encodeURIComponent(registrationNumber);
      const resp = await axiosInstance.get(`/students/${idEnc}/attendance/${regEnc}/subjects`);
      const subjectWise = Array.isArray(resp?.data?.subjectWise) ? resp.data.subjectWise : [];
      setSubjectWiseByStudent((prev) => ({ ...prev, [key]: subjectWise }));
    } catch (err) {
      console.error('Failed to load subject-wise attendance for student', studentId, err);
    } finally {
      setLoadingSubjectWise((prev) => ({ ...prev, [key]: false }));
    }
  };

  const toggleStudentExpanded = (studentId, registrationNumber) => {
    setExpandedStudents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });

    if (registrationNumber) {
      void ensureSubjectWiseLoaded(studentId, registrationNumber);
    }
  };

  const ensureSessionsLoadedForStudent = async (studentId, registrationNumber) => {
    const key = `${studentId}::${registrationNumber}`;

    if (sessionsByStudent[key]) {
      return sessionsByStudent[key];
    }

    if (loadingSessions[key]) {
      return null;
    }

    setLoadingSessions((prev) => ({ ...prev, [key]: true }));
    try {
      const idEnc = encodeURIComponent(studentId);
      const resp = await axiosInstance.get(`/students/${idEnc}/attendance`);
      const allSessions = Array.isArray(resp?.data?.attendance) ? resp.data.attendance : [];

      const filtered = allSessions.filter((s) => !registrationNumber || s.registrationNumber === registrationNumber);

      const dedupedMap = new Map();
      filtered.forEach((session) => {
        const dateKey = session.date ? new Date(session.date).toISOString().slice(0, 10) : String(session.date || '');
        const subjectKey = String(session.subjectName || '');
        const roomKey = String(session.roomName || '');
        const lecturerKey = String(session.lecturerName || '');
        const keyInner = `${dateKey}|${subjectKey}|${roomKey}|${lecturerKey}`;

        const existing = dedupedMap.get(keyInner);
        if (!existing) {
          dedupedMap.set(keyInner, session);
        } else {
          const isCurrentPresent = session.status === 'Present';
          const isExistingPresent = existing.status === 'Present';
          if (isCurrentPresent && !isExistingPresent) {
            dedupedMap.set(keyInner, session);
          }
        }
      });

      const dedupedSessions = Array.from(dedupedMap.values());

      dedupedSessions.sort((a, b) => {
        const dateA = a.date ? new Date(a.date) : null;
        const dateB = b.date ? new Date(b.date) : null;

        const isValidA = dateA && !Number.isNaN(dateA.getTime());
        const isValidB = dateB && !Number.isNaN(dateB.getTime());

        if (isValidA && isValidB && dateA.getTime() !== dateB.getTime()) {
          return dateB.getTime() - dateA.getTime();
        }

        if (!isValidA && isValidB) return 1;
        if (isValidA && !isValidB) return -1;

        const timeToMinutes = (value) => {
          if (!value) return -1;
          const [h, m] = String(value).split(':');
          const hours = Number.parseInt(h, 10);
          const minutes = Number.parseInt(m || '0', 10);
          if (Number.isNaN(hours)) return -1;
          return hours * 60 + (Number.isNaN(minutes) ? 0 : minutes);
        };

        const tA = timeToMinutes(a.startTime);
        const tB = timeToMinutes(b.startTime);

        if (tA !== tB) return tA - tB;

        return 0;
      });

      setSessionsByStudent((prev) => ({ ...prev, [key]: dedupedSessions }));
      return dedupedSessions;
    } catch (err) {
      console.error('Failed to load attendance sessions for student', studentId, err);
      return null;
    } finally {
      setLoadingSessions((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleOpenSubjectModal = async (student, subject) => {
    const studentKey = `${student.studentId}::${student.registrationNumber}`;
    const sessions = await ensureSessionsLoadedForStudent(student.studentId, student.registrationNumber);

    if (!sessions) {
      setSelectedSubjectModal({
        studentName: student.fullName,
        registrationNumber: student.registrationNumber,
        subjectName: subject.subjectName,
        subjectShortName: subject.subjectShortName,
        totalClasses: 0,
        attendedClasses: 0,
        absentClasses: 0,
        attendancePercentage: 0,
        sessions: [],
      });
      return;
    }

    const overrides = subjectWiseByStudent[studentKey] || [];
    const nameKey = String(subject.subjectName || '')
      .trim()
      .toLowerCase();
    const override = overrides.find((s) =>
      String(s.subjectName || '')
        .trim()
        .toLowerCase() === nameKey
    );

    const targetBatchSubjectId = override?.batchSubjectId ?? subject.batchSubjectId ?? null;

    const subjectSessions = sessions.filter((session) => {
      const matchByBatchSubjectId = targetBatchSubjectId && session.batchSubjectId === targetBatchSubjectId;
      const matchByName = nameKey && String(session.subjectName || '').trim().toLowerCase() === nameKey;
      return matchByBatchSubjectId || matchByName;
    });

    let totalClasses = 0;
    let attendedClasses = 0;
    let absentClasses = 0;

    subjectSessions.forEach((s) => {
      totalClasses += 1;
      if (s.status === 'Present') attendedClasses += 1;
      else if (s.status === 'Absent') absentClasses += 1;
    });

    const attendancePercentage = totalClasses > 0
      ? Math.round((attendedClasses / totalClasses) * 1000) / 10
      : 0;

    setSelectedSubjectModal({
      studentName: student.fullName,
      registrationNumber: student.registrationNumber,
      subjectName: subject.subjectName,
      subjectShortName: subject.subjectShortName,
      totalClasses,
      attendedClasses,
      absentClasses,
      attendancePercentage,
      sessions: subjectSessions,
    });
  };

  const handleCloseSubjectModal = () => {
    setSelectedSubjectModal(null);
  };

  const getPercentageColor = (percentage) => {
    if (percentage >= 75) return 'bg-emerald-500';
    if (percentage >= 50) return 'bg-amber-500';
    return 'bg-rose-500';
  };

  const getPercentageBadgeColor = (percentage) => {
    if (percentage >= 75) return 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200';
    if (percentage >= 50) return 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200';
    return 'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200';
  };

  // Filter students based on search query
  const filteredStudents = useMemo(() => {
    if (!attendanceData?.students) return [];
    
    if (!searchQuery.trim()) {
      return attendanceData.students;
    }

    const query = searchQuery.toLowerCase().trim();
    return attendanceData.students.filter(student => {
      const fullName = student.fullName.toLowerCase();
      const studentId = student.studentId.toLowerCase();
      const regNumber = student.registrationNumber.toLowerCase();
      
      return fullName.includes(query) || 
             studentId.includes(query) || 
             regNumber.includes(query);
    });
  }, [attendanceData?.students, searchQuery]);

  const buildGroupWhatsAppMessage = () => {
    const batchGroupName = attendanceData?.whatsappGroupName || 'this batch';
    const lowStudents = (attendanceData?.students || [])
      .filter((s) => s.attendance.overallPercentage <= 75)
      .sort((a, b) => a.attendance.overallPercentage - b.attendance.overallPercentage);

    const lines = [
      `📢 *Attendance Alert — ${batchGroupName}*`,
      ``,
      `Dear Students,`,
      ``,
      `The following students currently have attendance at or below *75%* and must improve their attendance urgently:`,
      ``,
      ...lowStudents.map(
        (s) =>
          `⚠️ *${s.fullName}* (${s.registrationNumber}) — *${s.attendance.overallPercentage.toFixed(1)}%*`
      ),
      ``,
      `The minimum required attendance is *75%*. Failing to meet this requirement will affect your academic completion — you will *not* be permitted to submit assignments or attend examinations.`,
      ``,
      `Please attend all upcoming sessions regularly.`,
      ``,
      `Regards,`,
      `Management`,
    ];
    return lines.join('\n');
  };

  const buildIndividualWhatsAppMessage = (student) => {
    const pct = student.attendance.overallPercentage.toFixed(1);
    const lines = [
      `Dear *${student.fullName}*,`,
      ``,
      `We would like to bring to your attention that your current attendance is *${pct}%*, which is at or below the required threshold of *75%*.`,
      ``,
      `📊 *Attendance Summary:*`,
      `• Total Classes: ${student.attendance.totalClasses}`,
      `• Classes Attended: ${student.attendance.attendedClasses}`,
      `• Classes Missed: ${student.attendance.absentClasses}`,
      ``,
      `We strongly advise you to attend all upcoming classes to improve your attendance. Failure to reach 75% will affect your academic completion, and you will *not* be permitted to submit assignments or attend examinations.`,
      ``,
      `Regards,`,
      `Management`,
    ];
    return lines.join('\n');
  };

  const handleCopyGroupMessage = async () => {
    const message = buildGroupWhatsAppMessage();
    try {
      await navigator.clipboard.writeText(message);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = message;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopiedGroupMsg(true);
    setTimeout(() => setCopiedGroupMsg(false), 2500);
  };

  const handleCopyIndividualMessage = async (student) => {
    const message = buildIndividualWhatsAppMessage(student);
    try {
      await navigator.clipboard.writeText(message);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = message;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopiedStudentId(student.studentId);
    setTimeout(() => setCopiedStudentId(null), 2500);
  };

  const groupedSubjectSessions = useMemo(() => {
    if (!selectedSubjectModal || !Array.isArray(selectedSubjectModal.sessions)) return [];

    const groups = new Map();

    selectedSubjectModal.sessions.forEach((session) => {
      const dateObj = session.date ? new Date(session.date) : null;
      const key = dateObj && !Number.isNaN(dateObj.getTime())
        ? dateObj.toISOString().slice(0, 10)
        : String(session.date || '');
      const label = formatDate(session.date);

      if (!groups.has(key)) {
        groups.set(key, { key, label, items: [] });
      }
      groups.get(key).items.push(session);
    });

    return Array.from(groups.values());
  }, [selectedSubjectModal]);

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
        {/* Page Header */}
        <div className="mb-8 border-b border-slate-200 pb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900 text-white">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Administration &rsaquo; Attendance</p>
                <h1 className="text-xl font-semibold tracking-tight text-slate-900">Student Attendance Monitoring</h1>
              </div>
            </div>
            <p className="text-sm text-slate-500">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>

        {/* Batch Selection Card */}
        <section className="mb-10 rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-600">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Step 1</p>
                <h2 className="text-base font-semibold text-slate-900">Select Batch</h2>
              </div>
            </div>
          </div>

          <div className="space-y-6 px-6 py-6">
            <div className="space-y-3">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-600" htmlFor="students-attendance-batch-select">
                  Batch
                </label>
                <Select
                  className="w-full"
                  inputId="students-attendance-batch-select"
                  options={options}
                  isClearable
                  placeholder="Search or pick a batch"
                  value={
                    selectedBatchId
                      ? (() => {
                          for (const group of options) {
                            const found = group.options.find((o) => o.value === selectedBatchId);
                            if (found) return found;
                          }
                          return null;
                        })()
                      : null
                  }
                  onChange={(newVal) => {
                    setSelectedBatchId(newVal ? newVal.value : '');
                  }}
                  classNamePrefix="rs"
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      minHeight: '48px',
                      borderRadius: '12px',
                      borderWidth: '1px',
                      borderColor: state.isFocused ? '#0f172a' : '#d0d7e3',
                      boxShadow: state.isFocused ? '0 0 0 4px rgba(15, 23, 42, 0.12)' : 'none',
                      backgroundColor: 'white',
                      fontSize: '14px',
                      '&:hover': {
                        borderColor: '#94a3b8',
                      },
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    menu: (base) => ({
                      ...base,
                      borderRadius: '12px',
                      overflow: 'hidden',
                      boxShadow: '0 18px 45px -20px rgba(15, 23, 42, 0.4)',
                      border: '1px solid #e2e8f0',
                    }),
                    menuList: (base) => ({
                      ...base,
                      padding: '6px',
                      maxHeight: '280px',
                    }),
                    group: (base) => ({
                      ...base,
                      paddingTop: '10px',
                      paddingBottom: '6px',
                    }),
                    groupHeading: (base) => ({
                      ...base,
                      fontSize: '11px',
                      fontWeight: 700,
                      color: '#475569',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      paddingLeft: '14px',
                      paddingRight: '14px',
                      paddingBottom: '6px',
                      marginBottom: '6px',
                      borderBottom: '1px solid #e2e8f0',
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected ? '#0f172a' : state.isFocused ? '#f8fafc' : 'white',
                      color: state.isSelected ? 'white' : '#0f172a',
                      padding: '10px 14px',
                      cursor: 'pointer',
                      fontWeight: state.isSelected ? 600 : 500,
                      fontSize: '14px',
                      borderRadius: '6px',
                      marginBottom: '2px',
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: '#94a3b8',
                      fontWeight: 400,
                      fontSize: '14px',
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: '#0f172a',
                      fontWeight: 600,
                      fontSize: '14px',
                    }),
                  }}
                />
              </div>
              <p className="text-sm text-slate-500">
                Select a batch to view consolidated attendance statistics and included sub-batches.
              </p>
            </div>

            {selectedBatchId && !loading && attendanceData && (
              <aside className="grid gap-6 rounded-xl border border-slate-200 bg-slate-50 p-6 lg:grid-cols-[1.4fr_0.8fr_1fr]">
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">WhatsApp group</p>
                  <p className="text-lg font-medium text-slate-900">
                    {attendanceData.whatsappGroupName || 'No WhatsApp Group'}
                  </p>
                  <p className="text-sm text-slate-500">
                    These metrics reflect the combined attendance footprint for the selected batch grouping.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Students</p>
                    <p className="mt-1 text-2xl font-semibold text-slate-900">{attendanceData.totalStudents}</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Batches</p>
                    <p className="mt-1 text-2xl font-semibold text-slate-900">{attendanceData.batches.length}</p>
                  </div>
                  <div className="col-span-2 rounded-lg border border-slate-200 bg-white px-4 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Students per batch</p>
                    <p className="mt-1 text-lg font-semibold text-slate-900">
                      {(attendanceData.totalStudents / Math.max(attendanceData.batches.length, 1)).toFixed(1)}
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <span>Included batches</span>
                    <span>{attendanceData.batches.length}</span>
                  </div>
                  <ul className="space-y-2 overflow-y-auto" style={{ maxHeight: '176px' }}>
                    {attendanceData.batches.map((b, idx) => (
                      <li key={b.batchId} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded bg-slate-900 text-[11px] font-semibold text-white">
                          {idx + 1}
                        </span>
                        <span className="flex-1 truncate">
                          {b.batchInternalName || b.batchName}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>
            )}
          </div>
        </section>

        {loading && (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white py-16 shadow-sm">
            <div className="relative flex h-14 w-14 items-center justify-center">
              <span className="absolute h-full w-full animate-spin rounded-full border-2 border-slate-200 border-t-slate-900"></span>
              <span className="inline-block h-4 w-4 rounded-full bg-slate-900"></span>
            </div>
            <p className="mt-6 text-base font-medium text-slate-900">Loading attendance data</p>
            <p className="mt-1 text-sm text-slate-500">Please wait while we fetch the latest records.</p>
          </div>
        )}

        {error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-6 py-5 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-600 text-white">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-rose-900">Unable to load data</h3>
                <p className="mt-1 text-sm text-rose-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {!loading && attendanceData && attendanceData.students.length === 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white px-8 py-12 text-center shadow-sm">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 text-slate-500">
              <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <h3 className="mt-6 text-lg font-semibold text-slate-900">No students found</h3>
            <p className="mt-2 text-sm text-slate-500">
              There are no active students registered in this batch. Double-check the selection or reach out to administration for support.
            </p>
          </div>
        )}

        {!loading && attendanceData && attendanceData.students.length > 0 && (
          <div className="space-y-8">
            {/* Search Bar */}
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="px-5 py-4">
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="block w-full rounded-lg border border-slate-300 py-2 pl-10 pr-10 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                    placeholder="Search by student name, student ID, or registration number..."
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 transition hover:text-slate-600"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                
                {searchQuery && (
                  <div className="mt-3 text-sm text-slate-500">
                    Found <span className="font-semibold text-slate-900">{filteredStudents.length}</span> of {attendanceData.students.length} students
                  </div>
                )}
              </div>
            </div>

            {/* Section Header */}
            <div className="flex items-center justify-between border-b border-slate-200 pb-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Overview</p>
                <h2 className="mt-0.5 text-lg font-semibold text-slate-900">Attendance Summary</h2>
              </div>
              <div className="text-right">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Total Records</p>
                <p className="mt-0.5 text-xl font-semibold text-slate-900">{filteredStudents.length}</p>
              </div>
            </div>

            {/* Statistics Summary */}
            <div className="grid gap-4 md:grid-cols-4">
              <div className="rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Total Students</p>
                  <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <p className="mt-3 text-3xl font-semibold text-slate-900">{filteredStudents.length}</p>
                <p className="mt-1 text-sm text-slate-500">{searchQuery ? 'Matching search' : 'Enrolled & active'}</p>
              </div>

              <div className="rounded-xl border-l-4 border border-emerald-400 bg-white px-5 py-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-emerald-600">Satisfactory</p>
                  <svg className="h-5 w-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="mt-3 text-3xl font-semibold text-slate-900">
                  {filteredStudents.filter(s => s.attendance.overallPercentage >= 75).length}
                </p>
                <p className="mt-1 text-sm text-slate-500">≥75% attendance</p>
              </div>

              <div className="rounded-xl border-l-4 border border-amber-400 bg-white px-5 py-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-amber-600">Borderline</p>
                  <svg className="h-5 w-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <p className="mt-3 text-3xl font-semibold text-slate-900">
                  {filteredStudents.filter(s => s.attendance.overallPercentage >= 50 && s.attendance.overallPercentage < 75).length}
                </p>
                <p className="mt-1 text-sm text-slate-500">50-74% attendance</p>
              </div>

              <div className="rounded-xl border-l-4 border border-rose-400 bg-white px-5 py-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-rose-600">At Risk</p>
                  <svg className="h-5 w-5 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="mt-3 text-3xl font-semibold text-slate-900">
                  {filteredStudents.filter(s => s.attendance.overallPercentage < 50).length}
                </p>
                <p className="mt-1 text-sm text-slate-500">&lt;50% attendance</p>
              </div>
            </div>

            {/* Students List Header */}
            <div className="flex items-center justify-between border-b border-slate-200 pb-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Records</p>
                <h3 className="mt-0.5 text-lg font-semibold text-slate-900">Student Attendance Records</h3>
                <p className="mt-0.5 text-xs text-slate-500">
                  {searchQuery ? `Showing ${filteredStudents.length} of ${attendanceData.students.length} student${filteredStudents.length !== 1 ? 's' : ''}` : 'Sorted by overall attendance — highest to lowest'}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {(() => {
                  const warnCount = (attendanceData?.students || []).filter(
                    (s) => s.attendance.overallPercentage <= 75
                  ).length;
                  return warnCount > 0 ? (
                    <button
                      type="button"
                      onClick={handleCopyGroupMessage}
                      className={`inline-flex items-center gap-2 rounded-lg border px-4 py-1.5 text-xs font-semibold transition ${
                        copiedGroupMsg
                          ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                          : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                      title="Copy WhatsApp group attendance alert for all at-risk students"
                    >
                      {copiedGroupMsg ? (
                        <>
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Copied!</span>
                        </>
                      ) : (
                        <>
                          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                          </svg>
                          <span>Copy Group Attendance Alert ({warnCount})</span>
                        </>
                      )}
                    </button>
                  ) : null;
                })()}
              </div>
            </div>

            {/* No Results Message */}
            {filteredStudents.length === 0 && searchQuery && (
              <div className="rounded-2xl border border-slate-200 bg-white px-8 py-10 text-center shadow-sm">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="mt-6 text-lg font-semibold text-slate-900">No students found</h3>
                <p className="mt-2 text-sm text-slate-500">
                  No students match "<span className="font-medium text-slate-900">{searchQuery}</span>". Try a different name or ID.
                </p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-6 inline-flex items-center justify-center rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                >
                  Clear search
                </button>
              </div>
            )}

            {/* Students List - Sorted by attendance percentage (high to low) */}
            {filteredStudents
              .slice()
              .sort((a, b) => b.attendance.overallPercentage - a.attendance.overallPercentage)
              .map((student, index) => {
                const initials = student.fullName
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase();
                const isExpanded = expandedStudents.has(student.studentId);
                const overallPercentage = student.attendance.overallPercentage;
                const statusLabel = overallPercentage >= 75 ? 'Excellent' : overallPercentage >= 50 ? 'On track' : 'Needs attention';
                const statusBadgeClass = overallPercentage >= 75
                  ? 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200'
                  : overallPercentage >= 50
                  ? 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200'
                  : 'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200';

                return (
                  <div key={student.studentId} className={`rounded-xl border-l-4 border bg-white p-6 shadow-sm transition-shadow duration-200 hover:shadow-md ${
                    overallPercentage >= 75 ? 'border-emerald-400' : overallPercentage >= 50 ? 'border-amber-400' : 'border-rose-400'
                  }`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded bg-slate-900 text-xs font-semibold text-white">
                          #{index + 1}
                        </span>
                        <div>
                          <h3 className="text-xl font-semibold text-slate-900">{student.fullName}</h3>
                          <div className="mt-3 flex flex-wrap gap-2 text-xs font-medium text-slate-600">
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 20 20">
                                <path d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" />
                              </svg>
                              {student.registrationNumber}
                            </span>
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 20 20">
                                <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                              </svg>
                              {student.courseShortName || student.courseName}
                            </span>
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd"></path>
                                <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z"></path>
                              </svg>
                              {student.batchName}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <div className={`inline-flex items-center justify-center rounded-lg px-5 py-2.5 text-2xl font-semibold tabular-nums ${getPercentageBadgeColor(overallPercentage)}`}>
                          {overallPercentage.toFixed(1)}%
                        </div>
                        <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Overall Attendance</p>
                        {overallPercentage <= 75 && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleCopyIndividualMessage(student); }}
                            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
                              copiedStudentId === student.studentId
                                ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                                : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                            }`}
                            title="Copy individual attendance alert message for this student"
                          >
                            {copiedStudentId === student.studentId ? (
                              <>
                                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span>Copied!</span>
                              </>
                            ) : (
                              <>
                                <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                </svg>
                                <span>Copy Warning</span>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="mt-6 flex flex-col gap-6 lg:flex-row">
                      <div className="flex flex-col items-center gap-3 lg:w-36">
                        <div className="h-36 w-28 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                          {student.hasImage && imageCache[student.studentId] && !imageErrors[student.studentId] ? (
                            <img
                              src={imageCache[student.studentId]}
                              alt={student.fullName}
                              className="h-full w-full object-cover"
                              onError={() => handleStudentImageError(student.studentId)}
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center bg-slate-900 text-white">
                              <span className="text-2xl font-semibold">{initials}</span>
                            </div>
                          )}
                        </div>
                        <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${statusBadgeClass}`}>
                          <span className="inline-block h-2 w-2 rounded-full bg-current"></span>
                          {statusLabel}
                        </span>
                      </div>

                      <div className="flex-1">
                        <div className="rounded-lg border border-slate-200 bg-slate-50 px-5 py-4">
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <span className="text-sm font-semibold text-slate-700">Attendance Overview</span>
                            <span className="text-sm font-medium text-slate-600">
                              {student.attendance.attendedClasses} / {student.attendance.totalClasses} classes
                            </span>
                          </div>
                          <div className="mt-3 h-3 w-full overflow-hidden rounded-full bg-slate-200">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${getPercentageColor(overallPercentage)}`}
                              style={{ width: `${overallPercentage}%` }}
                            ></div>
                          </div>
                          <div className="mt-3 flex flex-wrap gap-4 text-xs font-semibold text-slate-600">
                            <span className="inline-flex items-center gap-2">
                              <span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-500"></span>
                              Present <span className="text-slate-900">{student.attendance.attendedClasses}</span>
                            </span>
                            <span className="inline-flex items-center gap-2">
                              <span className="inline-block h-2.5 w-2.5 rounded-full bg-rose-500"></span>
                              Absent <span className="text-slate-900">{student.attendance.absentClasses}</span>
                            </span>
                            <span className="inline-flex items-center gap-2">
                              <span className="inline-block h-2.5 w-2.5 rounded-full bg-slate-400"></span>
                              Total <span className="text-slate-900">{student.attendance.totalClasses}</span>
                            </span>
                          </div>
                        </div>

                        <button
                          onClick={() => toggleStudentExpanded(student.studentId, student.registrationNumber)}
                          className="mt-4 flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                        >
                          <span>
                            Subject-wise Breakdown ({student.attendance.subjectWise.length})
                            {loadingSubjectWise[`${student.studentId}::${student.registrationNumber}`] && (
                              <span className="ml-2 text-xs font-normal text-slate-500">Loading…</span>
                            )}
                          </span>
                          <svg
                            className={`h-5 w-5 text-slate-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {isExpanded && student.attendance.subjectWise.length > 0 && (
                      <div className="mt-6 border-t border-slate-200 pt-6">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <h4 className="text-base font-semibold text-slate-900">Subject Performance</h4>
                          <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Sorted by attendance</span>
                        </div>
                        <div className="mt-4 grid gap-4 md:grid-cols-2">
                          {student.attendance.subjectWise.map((subject) => {
                            const studentKey = `${student.studentId}::${student.registrationNumber}`;
                            const overrides = subjectWiseByStudent[studentKey] || [];
                            const nameKey = String(subject.subjectName || '')
                              .trim()
                              .toLowerCase();
                            const override = overrides.find((s) =>
                              String(s.subjectName || '')
                                .trim()
                                .toLowerCase() === nameKey
                            );
                            const totalClasses = override?.totalClasses ?? subject.totalClasses ?? 0;
                            const attendedClasses = override?.attendedClasses ?? subject.attendedClasses ?? 0;
                            const absentClasses = override?.absentClasses ?? subject.absentClasses ?? 0;
                            const attendancePercentageRaw = override?.attendancePercentage ?? subject.attendancePercentage ?? 0;
                            const attendancePercentage = Number.isFinite(attendancePercentageRaw)
                              ? attendancePercentageRaw
                              : 0;

                            return (
                              <div
                                key={subject.subjectId || `${studentKey}::${nameKey}`}
                                className="cursor-pointer rounded-lg border-l-4 border border-slate-200 bg-white px-4 py-3 shadow-sm transition-shadow duration-200 hover:shadow-md"
                                onClick={() => handleOpenSubjectModal(student, subject)}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex-1">
                                    <p className="text-sm font-semibold text-slate-900">{subject.subjectName}</p>
                                    {subject.subjectShortName && (
                                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                                        {subject.subjectShortName}
                                      </p>
                                    )}
                                    <p className="mt-2 text-xs text-slate-600">
                                      {attendedClasses} of {totalClasses} classes attended
                                    </p>
                                  </div>
                                  <div className="ml-2 text-right">
                                    <span
                                      className={`inline-flex items-center justify-center rounded-xl px-3 py-1.5 text-lg font-semibold ${getPercentageBadgeColor(
                                        attendancePercentage
                                      )}`}
                                    >
                                      {attendancePercentage.toFixed(1)}%
                                    </span>
                                  </div>
                                </div>
                                <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-200">
                                  <div
                                    className={`h-full rounded-full transition-all duration-500 ${getPercentageColor(attendancePercentage)}`}
                                    style={{ width: `${attendancePercentage}%` }}
                                  ></div>
                                </div>
                                <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-600">
                                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-700">
                                    <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Total</span>
                                    <span className="text-slate-900">{totalClasses}</span>
                                  </span>
                                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 font-medium text-emerald-700">
                                    <span className="text-[11px] font-semibold uppercase tracking-wide">Present</span>
                                    <span>{attendedClasses}</span>
                                  </span>
                                  <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 font-medium text-rose-700">
                                    <span className="text-[11px] font-semibold uppercase tracking-wide">Absent</span>
                                    <span>{absentClasses}</span>
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}

        {selectedSubjectModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4 py-6 backdrop-blur-sm">
            <div className="flex w-full max-w-5xl max-h-[90vh] flex-col overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-slate-900/10">
              <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-slate-50 px-6 py-5">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Subject Attendance Detail</p>
                  <h3 className="mt-1 text-lg font-semibold text-slate-900">
                    {selectedSubjectModal.subjectName}
                    {selectedSubjectModal.subjectShortName && (
                      <span className="ml-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                        {selectedSubjectModal.subjectShortName}
                      </span>
                    )}
                  </h3>
                  <p className="mt-1 text-xs text-slate-500">
                    {selectedSubjectModal.studentName} · {selectedSubjectModal.registrationNumber}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`inline-flex items-center justify-center rounded-lg px-4 py-2 text-lg font-semibold tabular-nums ${getPercentageBadgeColor(
                      selectedSubjectModal.attendancePercentage
                    )}`}
                  >
                    {selectedSubjectModal.attendancePercentage.toFixed(1)}%
                  </span>
                  <button
                    type="button"
                    onClick={handleCloseSubjectModal}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Close
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-auto px-6 py-4">
                {/* Attendance Stats Summary */}
                <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-center">
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Total</p>
                    <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">{selectedSubjectModal.totalClasses}</p>
                  </div>
                  <div className="rounded-lg border-l-4 border border-emerald-400 bg-white px-4 py-3 text-center">
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-emerald-600">Present</p>
                    <p className="mt-1 text-2xl font-semibold tabular-nums text-emerald-700">{selectedSubjectModal.attendedClasses}</p>
                  </div>
                  <div className="rounded-lg border-l-4 border border-rose-400 bg-white px-4 py-3 text-center">
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-rose-600">Absent</p>
                    <p className="mt-1 text-2xl font-semibold tabular-nums text-rose-700">{selectedSubjectModal.absentClasses}</p>
                  </div>
                  <div className={`rounded-lg border px-4 py-3 text-center ${getPercentageBadgeColor(selectedSubjectModal.attendancePercentage)}`}>
                    <p className="text-[11px] font-semibold uppercase tracking-widest">Rate</p>
                    <p className="mt-1 text-2xl font-semibold tabular-nums">{selectedSubjectModal.attendancePercentage.toFixed(1)}%</p>
                  </div>
                </div>

                {selectedSubjectModal.sessions.length === 0 && (
                  <div className="flex h-full items-center justify-center text-sm text-slate-500">
                    No attendance records found for this subject.
                  </div>
                )}

                {selectedSubjectModal.sessions.length > 0 && (
                  <div className="space-y-4">
                    {groupedSubjectSessions.map((group) => (
                      <div key={group.key}>
                        <div className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-slate-400">
                          {group.label}
                        </div>
                        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                          <div className="grid grid-cols-[120px,1.6fr,1.1fr,1.1fr,0.9fr] gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2.5 text-[11px] font-semibold uppercase tracking-widest text-slate-400">
                            <div>Time</div>
                            <div>Subject</div>
                            <div>Room</div>
                            <div>Lecturer</div>
                            <div className="text-right">Status</div>
                          </div>
                          {group.items.map((session) => (
                            <div
                              key={session.attendanceId}
                              className="grid grid-cols-[120px,1.6fr,1.1fr,1.1fr,0.9fr] gap-2 border-t border-slate-100 px-3 py-2.5 text-xs text-slate-700 hover:bg-slate-50"
                            >
                              <div className="font-medium tabular-nums">
                                {formatTime(session.startTime)}
                                {session.endTime ? ` – ${formatTime(session.endTime)}` : ''}
                              </div>
                              <div className="truncate">{session.subjectName || '—'}</div>
                              <div className="truncate">{session.roomName || '—'}</div>
                              <div className="truncate">{session.lecturerName || '—'}</div>
                              <div className="text-right">
                                <span
                                  className={`inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                    session.status === 'Present'
                                      ? 'bg-emerald-50 text-emerald-700'
                                      : session.status === 'Absent'
                                      ? 'bg-rose-50 text-rose-700'
                                      : 'bg-slate-100 text-slate-700'
                                  }`}
                                >
                                  {session.status}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

